import { useEffect } from 'react';

/**
 * Detects Samsung Internet Browser's Night Mode and applies counter-measures.
 * Samsung's dark mode ignores the color-scheme meta tag and applies CSS filter
 * inversions at the rendering level, breaking custom-themed apps.
 *
 * This hook:
 * 1. Detects Samsung Browser via user agent
 * 2. Monitors for forced dark mode via prefers-color-scheme media query
 * 3. Adds a data attribute to <html> so CSS can target Samsung dark mode specifically
 * 4. Observes DOM mutations for Samsung's injected dark mode styles and removes them
 */
export const useSamsungDarkModeDefense = (): void => {
  useEffect(() => {
    const isSamsungBrowser = /SamsungBrowser/i.test(navigator.userAgent);
    if (!isSamsungBrowser) return;

    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyDefense = (isDark: boolean): void => {
      if (isDark) {
        document.documentElement.setAttribute('data-samsung-dark-mode', 'true');

        // Force light color scheme at the document level
        document.documentElement.style.setProperty('color-scheme', 'light only', 'important');

        // Samsung injects filter: invert() on the html/body - override it
        document.documentElement.style.setProperty('filter', 'none', 'important');
        document.body.style.setProperty('filter', 'none', 'important');
      } else {
        document.documentElement.removeAttribute('data-samsung-dark-mode');
        document.documentElement.style.removeProperty('color-scheme');
        document.documentElement.style.removeProperty('filter');
        document.body.style.removeProperty('filter');
      }
    };

    // Apply immediately
    applyDefense(darkModeQuery.matches);

    // Listen for changes (user toggles night mode while app is open)
    const handler = (e: MediaQueryListEvent): void => applyDefense(e.matches);
    darkModeQuery.addEventListener('change', handler);

    // Observe for Samsung-injected style elements that force dark mode.
    // Skip styles created by our own app (styled-components uses data-styled attribute).
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (let j = 0; j < mutation.addedNodes.length; j++) {
          const node = mutation.addedNodes[j];
          if (
            node instanceof HTMLStyleElement &&
            !node.hasAttribute('data-styled') &&
            node.textContent &&
            node.textContent.includes('prefers-color-scheme')
          ) {
            const sheet = node.sheet;
            if (sheet) {
              try {
                for (let i = sheet.cssRules.length - 1; i >= 0; i--) {
                  const rule = sheet.cssRules[i];
                  if (
                    rule instanceof CSSMediaRule &&
                    rule.conditionText?.includes('prefers-color-scheme: dark')
                  ) {
                    sheet.deleteRule(i);
                  }
                }
              } catch {
                // CORS or security restrictions on accessing stylesheet rules - safe to ignore
              }
            }
          }
        }
      }
    });

    observer.observe(document.head, { childList: true });

    return () => {
      darkModeQuery.removeEventListener('change', handler);
      observer.disconnect();
      document.documentElement.removeAttribute('data-samsung-dark-mode');
      document.documentElement.style.removeProperty('color-scheme');
      document.documentElement.style.removeProperty('filter');
      document.body.style.removeProperty('filter');
    };
  }, []);
};
